import { Flex, Overflow, Stepper, StepperProps } from '@servicetitan/anvil2';
import { ReactNode, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { WorkflowLayoutsStore } from './WorkflowLayoutsStore';
import { wrapper } from './StepperLayout.module.scss';

const FOOTER_HEIGHT = '64px';
const FOOTER_HEIGHT_LARGE = '200px';

function useAvailableHeight() {
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);

    const containerRef = useCallback((node: HTMLDivElement | null) => {
        setElement(node);
    }, []);

    useLayoutEffect(() => {
        if (!element) {
            return;
        }

        const update = () => {
            const top = element.getBoundingClientRect().top;
            setHeight(window.innerHeight - top);
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [element]);

    return { availableHeight: height, containerRef };
}

interface StepperLayoutProps {
    steps: {
        name: string;
        label: string;
        panelClassName?: string;
        panelComponent?: ReactNode;
        footerComponent?: ReactNode;
        buttonsComponent?: ReactNode;
        largeFooter?: boolean;
    }[];
    buttonsComponent?: ReactNode;
    footerComponent?: ReactNode;
    onComplete?: StepperProps['onComplete'];
}

export const StepperLayout = ({
    steps,
    buttonsComponent,
    footerComponent,
    onComplete,
}: StepperLayoutProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isStepperReady, setIsStepperReady] = useState(false);
    const { DefaultLayout } = WorkflowLayoutsStore.useStore();
    const { availableHeight, containerRef } = useAvailableHeight();

    const stepKey = steps.map(s => s.name).join('\0');

    // Reset stepper readiness and step index whenever the step structure changes.
    // The key prop on <Stepper> forces an internal remount; this effect keeps
    // StepperLayout's own state in sync and re-runs the anti-flicker delay.
    useEffect(() => {
        setIsStepperReady(false);
        setCurrentStep(0);
        const timer = setTimeout(() => {
            setIsStepperReady(true);
        }, 0);
        return () => clearTimeout(timer);
    }, [stepKey]);

    if (!isStepperReady || steps.length === 0) {
        return <DefaultLayout>{/* Loading... */}</DefaultLayout>;
    }

    const footerHeight = steps[currentStep]?.largeFooter ? FOOTER_HEIGHT_LARGE : FOOTER_HEIGHT;

    return (
        <Stepper
            key={stepKey}
            onChange={(ctx: any) => {
                setCurrentStep(ctx.currentStepIndex);
            }}
            onComplete={onComplete}
            style={{ overflow: 'auto', padding: '0', flexGrow: 1 }}
        >
            <DefaultLayout>
                <div ref={containerRef} style={{ width: '100%' }}>
                    <Flex
                        direction="column"
                        gap="5"
                        style={{
                            width: '100%',
                            height:
                                availableHeight && availableHeight > 0
                                    ? availableHeight
                                    : undefined,
                        }}
                    >
                        <Overflow
                            style={{
                                maxHeight: `calc(100% - ${footerHeight} - 1.25rem)`,
                            }}
                        >
                            <Flex direction="column" gap="4">
                                <Stepper.List>
                                    {steps.map(step => (
                                        <Stepper.Step
                                            key={step.name}
                                            id={`${step.name}-step`}
                                            controls={`${step.name}-panel`}
                                        >
                                            {step.label}
                                        </Stepper.Step>
                                    ))}
                                </Stepper.List>
                                {steps.map(step => (
                                    <Stepper.Panel
                                        key={step.name}
                                        id={`${step.name}-panel`}
                                        flexGrow={1}
                                        style={{ width: '100%' }}
                                        className={step.panelClassName}
                                    >
                                        <div className={wrapper}>{step.panelComponent}</div>
                                        {footerComponent}
                                    </Stepper.Panel>
                                ))}
                            </Flex>
                        </Overflow>
                        <div style={{ height: footerHeight, marginTop: 'auto' }}>
                            {steps[currentStep]?.buttonsComponent ?? buttonsComponent}
                        </div>
                    </Flex>
                </div>
            </DefaultLayout>
        </Stepper>
    );
};
